import type * as zod from 'zod';

import { DEFAULT_RECORDING_USER_ID } from '@sb/ai/constants';
import {
  createMappingFromHardwareKeyToEquipmentInfo,
  createRobotIdsMap,
} from '@sb/ai/helpers';
import type { StandardBotsRO1State } from '@sb/integrations/implementations/StandardBotsRO1/types';
import { makeNamespacedLog } from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import {
  RecorderStateStatusEnum,
  type RecorderConfig,
  type RecorderState,
  type ReferenceFrame,
  type StepDetails,
  type StepDetailsEntry,
  type StartRecordingRequest,
  type StopRecordingRequest,
} from '@sb/sb-robot-sdk/src/codegen/typescript-axios';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { EquipmentStateItem } from '../../RoutineRunnerState';
import type { StepFailure } from '../../types';
import type { StepAction, StepPlayArguments } from '../Step';
import Step from '../Step';
import type { StepKind } from '../StepKind';

import Arguments, { type TagValue } from './Arguments';
import Variables from './Variables';

const log = makeNamespacedLog('CreateRecordingStep');

// Type guard to check if a tag is a TagExpression
const isTagExpression = (
  tag: TagValue,
): tag is Extract<TagValue, { kind: 'TagExpression' }> => {
  return typeof tag !== 'string' && tag.kind === 'TagExpression';
};

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

/**
 * Context for recording failure from the primary bot's recorder state
 */
type PrimaryRecordingFailureContext = {
  source: 'primary';
  state?: RecorderState;
};

/**
 * Context for recording failure from a secondary bot
 */
type SecondaryRecordingFailureContext = {
  source: 'secondary';
  equipmentId?: string;
  alias?: string;
  reason?:
    | 'websocket_disconnected'
    | 'routine_failure'
    | 'routine_not_running'
    | 'routine_paused';
  websocketStatus?: string;
  routineRunnerState?: unknown;
};

/**
 * Context for recording failure due to an error during status check
 */
type ErrorRecordingFailureContext = {
  source: 'error';
  error?: unknown;
};

/**
 * Discriminated union of all possible recording failure contexts
 */
type RecordingFailureContext =
  | PrimaryRecordingFailureContext
  | SecondaryRecordingFailureContext
  | ErrorRecordingFailureContext;

/**
 * Generates a human-readable failure reason from a recording failure context
 */
function getRecordingFailureReason(context: RecordingFailureContext): string {
  switch (context.source) {
    case 'primary': {
      const { state } = context;
      let errorsMsg = '';

      if (state?.errors?.length) {
        errorsMsg = `errors: ${state.errors.join(', ')}`;
      }

      return `Recording failed or stopped (status=${state?.status ?? 'unknown'}). ${errorsMsg}`;
    }

    case 'secondary': {
      const botName = context.alias ?? context.equipmentId ?? 'unknown';

      switch (context.reason) {
        case 'websocket_disconnected':
          return `Secondary bot "${botName}" websocket connection lost during recording (status: ${context.websocketStatus ?? 'unknown'})`;
        case 'routine_failure':
          return `Secondary bot "${botName}" routine runner entered failure state during recording`;
        case 'routine_not_running':
          return `Secondary bot "${botName}" routine runner stopped during recording`;
        case 'routine_paused':
          return `Secondary bot "${botName}" routine runner paused during recording`;
        default:
          return `Secondary bot "${botName}" encountered an error during recording`;
      }
    }

    case 'error': {
      const errorMessage =
        (context.error as { message?: string })?.message ?? context.error;

      return `Recording status check failed: ${String(errorMessage)}`;
    }

    default:
      return 'Recording failed for unknown reason';
  }
}

export default class CreateRecordingStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = true;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public substeps: Array<Step<object, object>> = [];

  /**
   * Track whether recording was successfully started
   * @internal
   */
  private recordingInProgress: boolean = false;

  /**
   * Timeout handle for automatic recording termination
   * @internal
   */
  private timeoutHandle: NodeJS.Timeout | null = null;

  /**
   * Recording start timestamp for calculating step offsets
   * @internal
   */
  private recordingStartTime: number = 0;

  /**
   * Monitoring cleanup function
   * @internal
   */
  private cancelMonitoring: undefined | (() => void) = undefined;

  public initializeVariableState(): void {
    this.variables = {};
  }

  public hasSelfTimeout(): boolean {
    return false;
  }

  // Subclasses override to dispatch the recorder in a different mode (e.g.
  // CreateDemonstrationStep returns 'demonstration' so echo-bot routes the
  // recording to the demonstration_recordings table instead of skills).
  protected getRecordingMode(): 'standard' | 'demonstration' {
    return 'standard';
  }

  // Subclasses override to attach a reference frame to the recording (e.g.
  // CreateDemonstrationStep resolves the user-selected reference object's
  // world pose at record start so replay can anchor relative to it).
  // Returning a value here — or throwing — happens before the recorder is
  // configured, so an unresolvable reference fails the recording cleanly.
  protected async getReferenceFrame(): Promise<ReferenceFrame | undefined> {
    return undefined;
  }

  public async _setup(_args: StepPlayArguments): Promise<void> {
    const recorderApi = this.getRecorderApi();
    await recorderApi.clearHardwareConfigCache();
  }

  /**
   * Collect static string tags from the arguments
   * @internal
   */
  private collectStaticTags(): string[] {
    const staticTags: string[] = [];

    for (const tag of this.args.tags) {
      if (typeof tag === 'string') {
        staticTags.push(tag);
      }
    }

    return staticTags;
  }

  /**
   * Evaluate expression tags and return the resulting string tags
   * @internal
   */
  private async evaluateExpressionTags(): Promise<string[]> {
    const evaluatedExpressionTags: string[] = [];

    for (const tag of this.args.tags) {
      if (isTagExpression(tag)) {
        try {
          const value = await this.routineFrame.evaluateExpression(
            tag.expression,
            undefined,
            true, // noFinalFailAndThrow - don't fail routine
          );

          // Convert value to string if it's not already
          let stringValue: string;

          if (typeof value === 'string') {
            stringValue = value;
          } else if (value != null) {
            stringValue = String(value);
          } else {
            log.warn(
              'recording.tags',
              'Expression tag evaluated to null/undefined',
              {
                tag,
              },
            );

            continue;
          }

          if (stringValue.trim().length > 0) {
            evaluatedExpressionTags.push(stringValue.slice(0, 30)); // Enforce max length
          }
        } catch (error) {
          log.warn('recording.tags', 'Failed to evaluate expression tag', {
            error,
            tag,
          });
        }
      }
    }

    log.info('recording.tags', 'Evaluated expression tags', {
      evaluatedExpressionTags,
    });

    return evaluatedExpressionTags;
  }

  public async _stop({
    fail,
  }: Partial<StepPlayArguments> & Pick<StepAction, 'fail'>): Promise<void> {
    // Clear timeout if step is stopped early
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }

    this.cancelMonitoring?.();

    if (this.recordingInProgress) {
      const routineStatus = this.routineContext.getRoutineRunnerState().kind;
      const tags: string[] = [];

      // Add status tags for primary bot
      if (routineStatus === 'Failure') {
        tags.push('error');
      } else if (routineStatus !== 'RoutineRunning') {
        tags.push('stopped');
      }

      // Check secondary bot routine runner states if monitoring is enabled for bimanual tasks
      const secondaryBotTags = this.getSecondaryBotStatusTags();

      // Add secondary bot status tags to the tags array if they're not already present
      for (const tag of secondaryBotTags) {
        if (!tags.includes(tag)) {
          tags.push(tag);
        }
      }

      // Evaluate expression tags if possible (substeps may not have completed)
      const evaluatedExpressionTags = await this.evaluateExpressionTags();

      // Combine status tags and evaluated expression tags
      const allTags = [...evaluatedExpressionTags, ...tags];

      // Try to collect step details if substeps have started
      const stepDetails = this.collectStepTimings();

      await this.stopRecording({ tags: allTags, stepDetails, fail });
    } else {
      log.warn(
        '_stop',
        'Skipping stop record because recording is not in progress.',
        { recordingInProgress: this.recordingInProgress },
      );
    }
  }

  public async _play({ playSubSteps, fail }: StepPlayArguments): Promise<void> {
    if (this.routineContext.robot.robotKind !== 'live') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: this.getStepKind(),
        },
        failureReason: `${this.getStepKind()} step not supported in visualizer`,
      });
    }

    // Check for bimanual secondary bot state monitoring requirements
    const secondaryBotMonitoringError =
      this.validateSecondaryBotMonitoringRequirements();

    if (secondaryBotMonitoringError) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: this.getStepKind(),
        },
        failureReason: secondaryBotMonitoringError,
      });
    }

    try {
      const recorderApi = this.getRecorderApi();

      // Start recording before playing substeps
      await this.startRecording();

      // Store recording start time for calculating step offsets
      this.recordingStartTime = Date.now();

      // Set up timeout if specified
      if (this.args.timeout && this.args.timeout > 0) {
        this.timeoutHandle = setTimeout(async () => {
          log.warn(
            'recording.timeout',
            `Recording timeout reached (${this.args.timeout}s), stopping recording and failing routine`,
            {
              timeout: this.args.timeout,
              taskId: this.args.taskId,
            },
          );

          // Evaluate expression tags before stopping due to timeout
          const evaluatedExpressionTags = await this.evaluateExpressionTags();

          // Combine evaluated expression tags with timeout tag
          const allTags = [...evaluatedExpressionTags, 'timeout'];

          // Try to collect step details if substeps have started
          const stepDetails = this.collectStepTimings();

          await this.stopRecording({ tags: allTags, stepDetails, fail });

          // Fail the routine
          await fail({
            failure: {
              kind: FailureKind.StepPlayFailure,
              stepKind: this.getStepKind(),
            },
            failureReason: `Recording timeout exceeded (${this.args.timeout}s)`,
          });
        }, this.args.timeout * 1000); // Convert seconds to milliseconds
      }

      // Status monitoring - in case recording fails
      this.cancelMonitoring = this.startMonitoring(
        async () => {
          try {
            const res = await recorderApi.getRecorderState();

            const state = res.data;

            log.info('recording.status', 'Recording state', {
              state,
            });

            // Check primary bot recording state
            const primaryBotTriggered =
              state?.status !== RecorderStateStatusEnum.Recording &&
              state?.status !== RecorderStateStatusEnum.Initializing;

            if (primaryBotTriggered) {
              return {
                triggered: true,
                context: { source: 'primary', state },
              };
            }

            // Check secondary bot routine runner states if monitoring is enabled
            const secondaryBotIssue =
              this.checkSecondaryBotStatesForMonitoring();

            if (secondaryBotIssue) {
              return secondaryBotIssue;
            }

            return {
              triggered: false,
              context: state,
            };
          } catch (err) {
            // Consider transport/other errors as non-recording
            log.error('recording.status', 'Error fetching recorder state', err);

            return {
              triggered: true,
              context: { source: 'error', error: err },
            };
          }
        },
        {
          onTriggered: async ({ stopMonitoring }, context) => {
            stopMonitoring();

            // Yield briefly so that a concurrent fail() from a substep has
            // time to propagate before we read the routine state.
            await new Promise((resolve) => setTimeout(resolve, 200));

            // If the routine is no longer running (e.g., a substep already
            // called fail(), or the routine was stopped during the delay),
            // don't overwrite with the less-specific recording state error.
            const routineState =
              this.routineContext.getRoutineRunnerState().kind;

            if (routineState !== 'RoutineRunning') {
              log.info(
                'recording.monitoring',
                `Skipping recording failure report — routine is no longer running (state: ${routineState})`,
              );

              return;
            }

            try {
              const failureReason = getRecordingFailureReason(
                context as RecordingFailureContext,
              );

              await fail({
                failure: {
                  kind: FailureKind.StepPlayFailure,
                  stepKind: this.getStepKind(),
                },
                failureReason,
              });
            } catch (err) {
              await fail({
                failure: {
                  kind: FailureKind.StepPlayFailure,
                  stepKind: this.getStepKind(),
                },
                failureReason: `Recording status check failed: ${String((err as any)?.message ?? err)}`,
                error: err as any,
              });
            }
          },
        },
        { pollIntervalMs: 1000 },
      );
    } catch (error) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: this.getStepKind(),
        },
        failureReason: `${error?.message}`,
        error,
      });
    }

    await playSubSteps();

    // Clear timeout since substeps completed normally
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }

    // Recording stop, monitoring stop, etc. all happen gracefully when the step is stopped by routine runner.
  }

  public async _pause({ fail }: StepAction): Promise<void> {
    const routineState = this.routineContext.getRoutineRunnerState();

    // Check if this is an "expected" pause that should continue recording
    // Only relevant to primary bot
    if (routineState.kind === 'RoutineRunning') {
      const safeStepKinds: StepKind[] = ['Antigravity', 'WaitForConfirmation'];
      const { currentStepKind } = routineState;

      if (currentStepKind && safeStepKinds.includes(currentStepKind)) {
        log.info(
          '_pause',
          'Pausing but continuing recording (safe substep triggered pause)',
          { currentStepKind },
        );

        return; // Don't stop the recording
      }
    }

    log.warn(
      '_pause',
      'Pause on CreateRecording step is not supported, stopping step.',
    );

    await this._stop({ fail });
  }

  /**
   * Recursively collect all steps (including nested substeps) and their details
   * @param steps - Steps to collect from
   * @param parentStepIds - IDs of parent steps in the hierarchy
   * @returns StepDetails object containing the entries
   */
  private collectAllStepsRecursive(
    steps: Array<Step<object, object>>,
    parentStepIds: string[] = [],
  ): StepDetails {
    const now = Date.now();
    const allEntries: Array<StepDetailsEntry> = [];

    for (const step of steps) {
      // Get parent step IDs (excluding CreateRecording itself)
      const parentIds = parentStepIds.filter((id) => id !== this.id);

      // Serialize step arguments (limit size to avoid huge payloads)
      let stepArgs: string | undefined;

      try {
        const argsJson = JSON.stringify(step.args);

        // Limit to 10KB to avoid huge payloads
        if (argsJson.length <= 10000) {
          stepArgs = argsJson;
        }
      } catch (error) {
        log.warn('recording.stepTimings', 'Failed to serialize step args', {
          stepId: step.id,
          error,
        });
      }

      // Serialize step variables (limit size) - use toJSON() to get clean variables without stepKind tag
      let stepVariables: string | undefined;

      try {
        const { variables } = step.toJSON();

        if (variables && Object.keys(variables).length > 0) {
          const varsJson = JSON.stringify(variables);

          // Limit to 10KB
          if (varsJson.length <= 10000) {
            stepVariables = varsJson;
          }
        }
      } catch (error) {
        log.warn(
          'recording.stepTimings',
          'Failed to serialize step variables',
          {
            stepId: step.id,
            error,
          },
        );
      }

      // Use playHistory to emit one entry per play() invocation (handles loop iterations).
      // Additionally, if the step is currently mid-execution on a new iteration
      // (playStartTime differs from the last completed entry), include the in-progress state.
      const historyEntries: Array<{
        playStartTime: number;
        fullTimeMs: number;
        selfTimeMs: number;
        isCompleted: boolean;
      }> = step.playHistory.map((e) => ({ ...e, isCompleted: true }));

      const lastEntry = historyEntries[historyEntries.length - 1];

      const isInProgress =
        step.playStartTime > 0 &&
        (!lastEntry || step.playStartTime !== lastEntry.playStartTime);

      if (isInProgress) {
        historyEntries.push({
          playStartTime: step.playStartTime,
          fullTimeMs: step.timers.full.getTime(),
          selfTimeMs: step.timers.self.getTime(),
          isCompleted: false,
        });
      }

      for (const entry of historyEntries) {
        // Only include entries that were played during this recording
        if (entry.playStartTime < this.recordingStartTime) {
          continue;
        }

        const startOffsetSec =
          (entry.playStartTime - this.recordingStartTime) / 1000;

        // For completed entries, trust fullTimeMs even when 0 (sub-millisecond steps).
        // Only fall back to wall-clock elapsed time for in-progress entries.
        const elapsedMs =
          entry.fullTimeMs > 0 || entry.isCompleted
            ? entry.fullTimeMs
            : now - entry.playStartTime;

        const endOffsetSec =
          (entry.playStartTime - this.recordingStartTime + elapsedMs) / 1000;

        const selfTimeSec =
          entry.selfTimeMs > 0 || entry.isCompleted
            ? entry.selfTimeMs / 1000
            : undefined;

        allEntries.push({
          stepId: step.id,
          stepKind: step.getStepKind(),
          startOffsetSec,
          endOffsetSec,
          selfTimeSec,
          parentStepIds: parentIds,
          stepArgs,
          stepVariables,
        });
      }

      // Recursively collect nested substeps
      if (step.substeps && step.substeps.length > 0) {
        const nestedDetails = this.collectAllStepsRecursive(step.substeps, [
          ...parentStepIds,
          step.id,
        ]);

        allEntries.push(...nestedDetails.entries);
      }
    }

    return { entries: allEntries };
  }

  /**
   * Collect timing data from substeps (recursively including all nested substeps)
   * @returns StepDetails object containing the entries, or undefined if no timings
   */
  private collectStepTimings(): StepDetails | undefined {
    if (!this.substeps || this.substeps.length === 0) {
      log.info('recording.stepTimings', 'No substeps to collect timing from');

      return undefined;
    }

    const stepDetails = this.collectAllStepsRecursive(this.substeps, [this.id]);

    if (stepDetails.entries.length === 0) {
      log.info('recording.stepTimings', 'No step timings collected');

      return undefined;
    }

    log.info('recording.stepTimings', 'Collected step timings', {
      count: stepDetails.entries.length,
      timings: stepDetails.entries.map((t: StepDetailsEntry) => ({
        stepId: t.stepId,
        stepKind: t.stepKind,
        startOffsetSec: t.startOffsetSec,
        endOffsetSec: t.endOffsetSec,
        selfTimeSec: t.selfTimeSec,
        parentStepIds: t.parentStepIds,
        hasArgs: !!t.stepArgs,
        hasVariables: !!t.stepVariables,
      })),
    });

    return stepDetails;
  }

  private async startRecording() {
    log.info('recording.start', 'Starting recording for CreateRecording step', {
      taskId: this.args.taskId,
    });

    const allEquipmentState = this.routineContext.equipment.getEquipmentState();

    const robotIdsMap = createRobotIdsMap(
      this.args.hardwareConfigEquipmentMapping,
      this.routineContext.robotId || 'local',
      allEquipmentState,
    );

    const equipmentKeyToEquipmentInfo =
      createMappingFromHardwareKeyToEquipmentInfo(
        this.args.hardwareConfigEquipmentMapping,
      );

    const equipmentKeyAssignments = Object.entries(
      equipmentKeyToEquipmentInfo,
    ).reduce(
      (acc, [key, equipmentInfo]) => {
        acc[key] = {
          equipmentId: equipmentInfo.equipmentId,
          ...(equipmentInfo.feed ? { feed: equipmentInfo.feed } : {}),
        };

        return acc;
      },
      {} as Record<string, { equipmentId: string; feed?: string }>,
    );

    const botIds = Object.values(robotIdsMap);

    const secondaryBotIds = botIds.filter(
      (id) => id !== this.routineContext.robotId,
    );

    try {
      const recorderApi = this.getRecorderApi();

      // Only add static string tags at the start - expression tags will be evaluated at the end
      const staticTags = this.collectStaticTags();

      // Enable secondary bots if any are specified in the hardware config mapping
      await this.ensureSecondaryBotsEnabled(recorderApi, secondaryBotIds);

      // First, configure the recording with task details
      const configureRequest: RecorderConfig = {
        taskId: this.args.taskId || '',
        userId: this.args.userId || DEFAULT_RECORDING_USER_ID,
        isSecondary: false,
        bots: secondaryBotIds,
        sensors: [],
        equipmentKeyAssignments,
        tags: ['routine', ...staticTags], // Tag this as a routine recording with static tags
        recordingMode: this.getRecordingMode(),
        referenceFrame: await this.getReferenceFrame(),
      };

      log.info('recording.configure', 'Configuring recording', {
        taskId: this.args.taskId,
        equipmentKeyAssignments,
        tags: ['routine', ...staticTags],
      });

      const configureResponse =
        await recorderApi.updateRecording(configureRequest);

      if (!configureResponse.data.success) {
        const errors =
          configureResponse.data.errors ||
          configureResponse.data.state?.errors ||
          [];

        log.error('recording.configure', 'Failed to configure recording', {
          errors,
        });

        throw new Error(
          `Failed to configure recording: ${Array.isArray(errors) ? errors.join(', ') : String(errors)}`,
        );
      }

      log.info('recording.configure', 'Successfully configured recording');

      // Then start the actual recording
      const recordingStartTime = new Date();

      const startRecordingRequest: StartRecordingRequest = {
        startTime: recordingStartTime.toISOString(),
        equipmentKeyAssignments,
      };

      log.info('recording.start', 'Starting recording via recorder API');

      this.recordingInProgress = true;

      const startRecordingResponse = await recorderApi.startRecording(
        startRecordingRequest,
      );

      const startState = startRecordingResponse.data.state;

      if (!startRecordingResponse.data.success) {
        throw new Error('Failed to start recording via recorder API');
      }

      if (
        !startState ||
        (startState.status !== RecorderStateStatusEnum.Recording &&
          startState.status !== RecorderStateStatusEnum.Initializing)
      ) {
        log.error('recording.start', 'Failed to start recording', {
          status: startState?.status,
          errors: startState?.errors,
        });

        throw new Error(
          `Failed to start recording: ${startState?.status ?? 'unknown'}. errors: ${startState?.errors?.join(', ')}`,
        );
      }

      log.info(
        'recording.start',
        'Successfully started recording via echo-bot',
      );
    } catch (error) {
      log.error(
        'recording.start',
        'Failed to start recording via recorder API',
        error,
      );

      this.recordingInProgress = false;

      throw error;
    }
  }

  /**
   * Ensures that secondary bots specified in the hardware config mapping are enabled
   * for recording, and disables any bots that are enabled but not specified.
   *
   * @param recorderApi - The recorder API instance
   * @param secondaryBotIds - Array of secondary bot IDs to enable
   * @throws Error if any bot enablement/disablement fails or if recorder state cannot be retrieved
   */
  private async ensureSecondaryBotsEnabled(
    recorderApi: ReturnType<typeof this.getRecorderApi>,
    secondaryBotIds: string[],
  ): Promise<void> {
    const currentStateResponse = await recorderApi.getRecorderState();
    const currentState = currentStateResponse.data;
    const enabledBots = currentState?.config?.bots || [];

    // Handle both string[] and RecorderBotDetails[] formats
    const enabledBotIdStrings = enabledBots
      .map((bot: string | { botId?: string; isEnabled?: boolean }) => {
        if (typeof bot === 'string') {
          return bot;
        }

        if (bot.isEnabled) {
          return bot.botId;
        }

        return undefined;
      })
      .filter((id: string | undefined): id is string => Boolean(id));

    log.info('recording.enableBot', 'Checking secondary bots', {
      secondaryBotIds,
      enabledBotIdStrings,
    });

    // Collect bots that need to be enabled
    const botsToEnable = secondaryBotIds.filter(
      (botId) => !enabledBotIdStrings.includes(botId),
    );

    // Log bots that are already enabled
    const alreadyEnabledBots = secondaryBotIds.filter((botId) =>
      enabledBotIdStrings.includes(botId),
    );

    for (const botId of alreadyEnabledBots) {
      log.info(
        'recording.enableBot',
        `Secondary bot ${botId} is already enabled`,
      );
    }

    // Collect bots that need to be disabled
    const botsToDisable = enabledBotIdStrings.filter(
      (botId: string) => !secondaryBotIds.includes(botId),
    );

    // Enable all bots in parallel
    const enablePromises = botsToEnable.map(async (botId) => {
      try {
        await recorderApi.enableRecorderBot({ botId, enabled: true });
        log.info('recording.enableBot', `Enabled secondary bot ${botId}`);
      } catch (enableError) {
        log.error(
          'recording.enableBot',
          `Failed to enable bot ${botId}`,
          enableError,
        );

        throw new Error(
          `Failed to enable secondary bot ${botId} for recording: ${(enableError as Error)?.message ?? String(enableError)}`,
        );
      }
    });

    // Disable all bots in parallel
    const disablePromises = botsToDisable.map(async (botId: string) => {
      try {
        await recorderApi.enableRecorderBot({ botId, enabled: false });
        log.info('recording.enableBot', `Disabled secondary bot ${botId}`);
      } catch (disableError) {
        log.error(
          'recording.enableBot',
          `Failed to disable bot ${botId}`,
          disableError,
        );

        throw new Error(
          `Failed to disable secondary bot ${botId}: ${(disableError as Error)?.message ?? String(disableError)}`,
        );
      }
    });

    // Execute all enable and disable operations in parallel
    try {
      await Promise.all([...enablePromises, ...disablePromises]);
    } catch (error) {
      const errorMessage =
        (error as Error)?.message ?? String(error) ?? 'Unknown error';

      log.error(
        'recording.enableBot',
        'Failed to enable/disable secondary bots',
        {
          error,
          errorMessage,
          botsToEnable,
          botsToDisable,
        },
      );

      throw new Error(
        `Failed to configure secondary bots for bimanual recording: ${errorMessage}`,
      );
    }
  }

  private async stopRecording({
    tags = [],
    stepDetails,
    fail,
  }: {
    tags?: string[];
    stepDetails?: StepDetails;
    fail: (failure: StepFailure) => Promise<void>;
  }) {
    if (this.recordingInProgress) {
      try {
        const recorderApi = this.getRecorderApi();

        this.recordingInProgress = false;

        const stopRequest: StopRecordingRequest = {
          delete: !this.args.uploadRecording,
          tags,
          stepDetails,
        };

        const stopResponse = await recorderApi.stopRecording(stopRequest);

        log.info('recording.stop', 'Stopped recording', {
          data: stopResponse.data,
        });
      } catch (recordingError) {
        const errorMessage =
          (recordingError as Error)?.message || 'Unknown error';

        await fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: this.getStepKind(),
          },
          failureReason: `Failed to stop recording: ${errorMessage}`,
          error: recordingError as Error,
        });
      }
    } else {
      log.info('recording.stop', 'Recording not started, skipping stop');
    }
  }

  /**
   * Gets the secondary bot equipment (StandardBotsRO1) from the hardware config mapping.
   * These are robots that are not the primary robot and are configured as StandardBotsRO1 equipment.
   *
   * @param allEquipmentState - The current state of all equipment
   * @returns Array of equipment items that are secondary bots
   */
  private getSecondaryBotEquipment(): EquipmentStateItem[] {
    const allEquipmentState = this.routineContext.equipment.getEquipmentState();

    const secondaryBotEquipment: EquipmentStateItem[] = [];

    for (const entry of Object.values(
      this.args.hardwareConfigEquipmentMapping,
    )) {
      // Skip the primary robot (robot_0 with no robotEquipmentId)
      if (entry.robotEquipmentId == null) {
        continue;
      }

      const equipment = allEquipmentState.find(
        (device) => device.id === entry.robotEquipmentId,
      );

      if (equipment && equipment.kind === 'StandardBotsRO1') {
        secondaryBotEquipment.push(equipment);
      }
    }

    return secondaryBotEquipment;
  }

  /**
   * Checks secondary bot routine runner states and returns candidate tags to add.
   * Only checks if monitorSecondaryBotState is enabled and the task is bimanual.
   *
   * @returns Set of candidate tags ('error', 'stopped') based on secondary bot states
   */
  private getSecondaryBotStatusTags(): Set<string> {
    const tags = new Set<string>();

    const secondaryBotEquipment = this.getSecondaryBotEquipment();
    const isBimanual = secondaryBotEquipment.length > 0;

    if (!this.args.monitorSecondaryBotState || !isBimanual) {
      return tags;
    }

    for (const equipment of secondaryBotEquipment) {
      const state = equipment.state as StandardBotsRO1State;
      const { routineRunnerState, alias } = state;

      if (routineRunnerState?.kind === 'Failure') {
        tags.add('error');

        log.warn('_stop', 'Secondary bot routine runner in failure state', {
          equipmentId: equipment.id,
          alias,
          routineRunnerState,
        });
      } else if (routineRunnerState?.kind !== 'RoutineRunning') {
        tags.add('stopped');

        log.warn('_stop', 'Secondary bot routine runner not running', {
          equipmentId: equipment.id,
          alias,
          routineRunnerState,
        });
      }
    }

    return tags;
  }

  /**
   * Validates that secondary bot monitoring requirements are met.
   * Checks feature flag and websocket connection status for all secondary bots.
   *
   * @returns Error message if validation fails, null if validation passes
   */
  private validateSecondaryBotMonitoringRequirements(): string | null {
    const secondaryBotEquipment = this.getSecondaryBotEquipment();
    const isBimanual = secondaryBotEquipment.length > 0;

    if (!this.args.monitorSecondaryBotState || !isBimanual) {
      return null;
    }

    // Check that the secondaryBotWebsocketState feature flag is enabled
    if (!getFeatureFlagSync('secondaryBotWebsocketState', false)) {
      return 'Secondary bot state monitoring requires the secondaryBotWebsocketState feature flag to be enabled';
    }

    // Check that all secondary bots have websocket connections established
    for (const equipment of secondaryBotEquipment) {
      const state = equipment.state as StandardBotsRO1State;
      const wsStatus = state.websocketConnectionStatus;

      if (!wsStatus || wsStatus.kind !== 'connected') {
        const statusKind = wsStatus?.kind ?? 'undefined';

        const errorMsg =
          wsStatus?.kind === 'failure'
            ? `: ${(wsStatus as { kind: 'failure'; error: string }).error}`
            : '';

        return `Secondary bot ${state.alias ?? equipment.id} websocket connection is not established (status: ${statusKind}${errorMsg}). Ensure the secondary bot is connected and the websocket connection is active.`;
      }
    }

    log.info(
      '_play',
      'Secondary bot state monitoring enabled for bimanual task',
      {
        secondaryBotCount: secondaryBotEquipment.length,
        secondaryBots: secondaryBotEquipment.map((eq) => ({
          id: eq.id,
          alias: (eq.state as StandardBotsRO1State).alias,
        })),
      },
    );

    return null;
  }

  /**
   * Checks secondary bot states during recording monitoring.
   * Returns a triggered result if any secondary bot has an issue, null otherwise.
   *
   * @returns Triggered result with context if issue found, null if all bots are healthy
   */
  private checkSecondaryBotStatesForMonitoring(): {
    triggered: true;
    context: {
      source: 'secondary';
      equipmentId: string;
      alias?: string;
      reason:
        | 'websocket_disconnected'
        | 'routine_failure'
        | 'routine_not_running'
        | 'routine_paused'
        | undefined;
      websocketStatus?: string;
      routineRunnerState?: unknown;
    };
  } | null {
    if (!this.args.monitorSecondaryBotState) {
      return null;
    }

    for (const equipment of this.getSecondaryBotEquipment()) {
      const botState = equipment.state as StandardBotsRO1State;
      const { routineRunnerState, websocketConnectionStatus, alias } = botState;

      // Check websocket connection status
      if (
        !websocketConnectionStatus ||
        websocketConnectionStatus.kind !== 'connected'
      ) {
        log.warn(
          'recording.status',
          'Secondary bot websocket disconnected during recording',
          {
            equipmentId: equipment.id,
            alias,
            status: websocketConnectionStatus?.kind,
          },
        );

        return {
          triggered: true,
          context: {
            source: 'secondary',
            equipmentId: equipment.id,
            alias,
            reason: 'websocket_disconnected',
            websocketStatus: websocketConnectionStatus?.kind,
          },
        };
      }

      if (!routineRunnerState) {
        log.warn(
          'recording.status',
          'Secondary bot routine runner state not available',
          {
            equipmentId: equipment.id,
            alias,
          },
        );

        return {
          triggered: true,
          context: {
            source: 'secondary',
            equipmentId: equipment.id,
            alias,
            reason: 'websocket_disconnected',
          },
        };
      }

      // Check routine runner state for failure
      if (routineRunnerState.kind === 'Failure') {
        log.warn(
          'recording.status',
          'Secondary bot routine runner in failure state',
          {
            equipmentId: equipment.id,
            alias,
            routineRunnerState,
          },
        );

        return {
          triggered: true,
          context: {
            source: 'secondary',
            equipmentId: equipment.id,
            alias,
            reason: 'routine_failure',
            routineRunnerState,
          },
        };
      }

      // Check if routine runner is not in RoutineRunning state
      if (routineRunnerState.kind !== 'RoutineRunning') {
        log.warn(
          'recording.status',
          'Secondary bot routine runner not running',
          {
            equipmentId: equipment.id,
            alias,
            routineRunnerState,
          },
        );

        return {
          triggered: true,
          context: {
            source: 'secondary',
            equipmentId: equipment.id,
            alias,
            reason: 'routine_not_running',
            routineRunnerState,
          },
        };
      }

      // Check if routine is paused - allow if paused due to safe step kinds
      if (routineRunnerState.isPaused) {
        const safeStepKinds: StepKind[] = [
          'Antigravity',
          'WaitForConfirmation',
        ];

        const { currentStepKind } = routineRunnerState;

        if (currentStepKind && safeStepKinds.includes(currentStepKind)) {
          log.info(
            'recording.status',
            'Secondary bot paused but continuing recording (safe substep triggered pause)',
            {
              equipmentId: equipment.id,
              alias,
              currentStepKind,
            },
          );

          // Continue to next equipment check, don't trigger failure
          continue;
        }

        log.warn('recording.status', 'Secondary bot routine is paused', {
          equipmentId: equipment.id,
          alias,
          routineRunnerState,
        });

        return {
          triggered: true,
          context: {
            source: 'secondary',
            equipmentId: equipment.id,
            alias,
            reason: 'routine_paused',
            routineRunnerState,
          },
        };
      }
    }

    return null;
  }

  private getRecorderApi() {
    const { sdk } = this.routineContext;

    if (!sdk) {
      log.error('recording.stop', 'Public API SDK not available');

      throw new Error('Public API SDK not available');
    }

    const recorderApi = sdk.recorder;

    if (!recorderApi) {
      log.error('recording.api', 'Recorder API not available on SDK');

      throw new Error('Recorder API not available on SDK');
    }

    return recorderApi;
  }
}
